import React from 'react';
import Container from './Container';
import ParticleField from './ParticleField';

/*
 * Centered detail-page hero with a civil/physics decor layer per `variant`:
 *   structural    → self-drawing Warren truss + pulsing load vectors
 *   infrastructure→ self-drawing suspension bridge + a light crossing the deck
 *   research      → an atom (nucleus + orbiting electrons)
 * All variants also get a blueprint grid, a drifting particle field, and an
 * engineering "dimension line" under the title. Title/lead stay data-driven.
 */
function StructuralTruss() {
  return (
    <svg className="truss" viewBox="0 0 800 170" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
      <path
        className="truss__frame"
        d="M0 150 L800 150 M80 60 L720 60 M0 150 L80 60 L200 150 L320 60 L440 150 L560 60 L680 150 L720 60 L800 150 M80 60 L200 150 L320 60 M320 60 L440 150 L560 60 M560 60 L680 150 L720 60"
      />
      <g className="truss__verticals">
        <line x1="200" y1="150" x2="200" y2="103" />
        <line x1="320" y1="60" x2="320" y2="105" />
        <line x1="440" y1="150" x2="440" y2="103" />
        <line x1="560" y1="60" x2="560" y2="105" />
        <line x1="680" y1="150" x2="680" y2="103" />
      </g>
      <g className="truss__loads">
        {[200, 320, 440, 560, 680].map((x, i) => (
          <g key={x} className="truss__load" style={{ '--ld': `${i * 0.3}s` }}>
            <line x1={x} y1="20" x2={x} y2="48" />
            <polygon points={`${x - 5},42 ${x + 5},42 ${x},52`} />
          </g>
        ))}
      </g>
      <g className="truss__nodes">
        {[[80, 60], [200, 150], [320, 60], [440, 150], [560, 60], [680, 150], [720, 60]].map(([cx, cy]) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3.5" />
        ))}
      </g>
    </svg>
  );
}

function BridgeDecor() {
  return (
    <svg className="bridge" viewBox="0 0 800 200" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
      <path
        className="bridge__frame"
        d="M30 150 L770 150
           M240 150 L240 44 M560 150 L560 44
           M30 120 C120 48 200 44 240 44 C320 44 360 98 400 98 C440 98 480 44 560 44 C600 44 680 48 770 120
           M300 72 L300 150 M350 86 L350 150 M400 98 L400 150 M450 86 L450 150 M500 72 L500 150"
      />
      <g className="bridge__nodes">
        {[[240, 44], [560, 44], [400, 98], [30, 120], [770, 120]].map(([cx, cy]) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3.5" />
        ))}
      </g>
      <circle className="bridge__car" r="4">
        <animateMotion dur="3.4s" repeatCount="indefinite" path="M30 150 L770 150" />
      </circle>
    </svg>
  );
}

function AtomDecor() {
  return (
    <svg className="atom" viewBox="0 0 520 300" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {[0, 60, 120].map((rot, i) => (
        <g key={rot} transform={`rotate(${rot} 260 150)`}>
          <ellipse className="atom__orbit" cx="260" cy="150" rx="210" ry="58" />
          <circle className="atom__electron" r="4.5">
            <animateMotion
              dur={`${3 + i * 0.6}s`}
              repeatCount="indefinite"
              path="M50,150 a210,58 0 1,0 420,0 a210,58 0 1,0 -420,0"
            />
          </circle>
        </g>
      ))}
      <circle className="atom__nucleus" cx="260" cy="150" r="7" />
    </svg>
  );
}

export default function PageHero({ white, accent, accentFirst = true, lead, variant }) {
  return (
    <section className={`page-hero${variant ? ` page-hero--${variant}` : ''}`}>
      <div className="page-hero__decor" aria-hidden="true">
        <div className="page-hero__grid" />
        <ParticleField count={16} />
        {variant === 'structural' && <StructuralTruss />}
        {variant === 'infrastructure' && <BridgeDecor />}
        {variant === 'research' && <AtomDecor />}
      </div>

      <Container>
        <div className="page-hero__inner">
          <h1 className="page-hero__title">
            {accentFirst ? (
              <>
                <span className="accent">{accent}</span> {white}
              </>
            ) : (
              <>
                {white} <span className="accent">{accent}</span>
              </>
            )}
          </h1>
          <span className="page-hero__dim" aria-hidden="true">
            <i />
            <em />
            <i />
          </span>
          {lead && <p className="page-hero__lead">{lead}</p>}
        </div>
      </Container>
    </section>
  );
}
